import {
  CarrierServicingGrantInputDto,
  CourierLoginMethod,
  CreateCarrierOrganisationDto,
  CreateProjectDto,
  CreateProjectDtoProjectType,
  KioskTransactionType,
  KnownCourierLoginMethod,
  KnownCreateProjectDtoProjectType,
  KnownKioskTransactionType
} from '@groundfloor-platform/mgmt-service-g-sdk';

/** What a carrier dropping parcels off needs. Every network selected starts with this. */
export const DEFAULT_SERVICING_PERMISSION = KnownKioskTransactionType.DeliverInboundParcel;

/** The other thirteen, kept behind an "advanced" disclosure rather than on the main path. */
export const ADVANCED_TRANSACTION_TYPES: KioskTransactionType[] = Object.values(KnownKioskTransactionType).filter(
  (type) => type !== DEFAULT_SERVICING_PERMISSION
);

/** "deliver-inbound-parcel" reads as "Deliver inbound parcel" in the picker. */
export function formatTransactionType(type: KioskTransactionType): string {
  const words = type.split('-').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Which credential this organisation's couriers sign in with. Inherited by every carrier beneath
 * it, and it drives which login tab the add-courier form opens on. Worded as the courier form
 * words it, so the two agree.
 */
export const LOGIN_METHOD_OPTIONS: { value: CourierLoginMethod; label: string }[] = [
  { value: KnownCourierLoginMethod.Mobile, label: 'Mobile number' },
  { value: KnownCourierLoginMethod.Id, label: 'ID/username' },
  { value: KnownCourierLoginMethod.AccessCode, label: 'ID/username & access code' }
];

/**
 * A stored `loginMethod` as the UI words it. The values are camelCase rather than hyphenated, so
 * there is nothing to derive a label from — the options list is the single source of the wording.
 */
export function formatLoginMethod(loginMethod: string): string {
  return LOGIN_METHOD_OPTIONS.find((option) => option.value === loginMethod)?.label ?? loginMethod;
}

/** What couriers use unless told otherwise, and what the add-courier form falls back to. */
export const DEFAULT_LOGIN_METHOD: CourierLoginMethod = KnownCourierLoginMethod.Mobile;

/** Letters and numbers only: the shortcode ends up in a URL. */
const SHORTCODE_PATTERN = /^[A-Za-z0-9]+$/;

export interface AddCarrierDetails {
  /** The carrier's trading name. Public: it appears on kiosks and on delivery records. */
  name: string;
  /** Optional. Empty means "don't send one". */
  shortcode: string;
}

export const EMPTY_DETAILS: AddCarrierDetails = { name: '', shortcode: '' };

export function validateName(name: string): string | undefined {
  return name.trim() ? undefined : 'Enter a name for the carrier';
}

export function validateShortcode(shortcode: string): string | undefined {
  const trimmed = shortcode.trim();
  if (!trimmed) {
    return undefined;
  }
  return SHORTCODE_PATTERN.test(trimmed) ? undefined : 'Use letters and numbers only';
}

export function isDetailsStepComplete(details: AddCarrierDetails): boolean {
  return !validateName(details.name) && !validateShortcode(details.shortcode);
}

/** No grant leaves a carrier invisible on every kiosk, and a network with none grants nothing. */
export function isNetworksStepComplete(servicing: CarrierServicingGrantInputDto[]): boolean {
  return servicing.length > 0 && servicing.every((grant) => grant.permissions.length > 0);
}

/** Selects a network seeded with the default permission, or clears it. */
export function toggleNetworkSelection(
  servicing: CarrierServicingGrantInputDto[],
  projectID: string
): CarrierServicingGrantInputDto[] {
  if (servicing.some((grant) => grant.projectID === projectID)) {
    return servicing.filter((grant) => grant.projectID !== projectID);
  }
  return [...servicing, { projectID, permissions: [DEFAULT_SERVICING_PERMISSION] }];
}

export function setNetworkPermissions(
  servicing: CarrierServicingGrantInputDto[],
  projectID: string,
  permissions: KioskTransactionType[]
): CarrierServicingGrantInputDto[] {
  return servicing.map((grant) => (grant.projectID === projectID ? { ...grant, permissions } : grant));
}

export function getNetworkPermissions(
  servicing: CarrierServicingGrantInputDto[],
  projectID: string
): KioskTransactionType[] {
  return servicing.find((grant) => grant.projectID === projectID)?.permissions ?? [];
}

/** Step 1 of two. The type and parent are the wizard's to supply, never the user's. */
export function toCreateProjectDto(
  details: AddCarrierDetails,
  parentProjectID: string,
  projectType: CreateProjectDtoProjectType
): CreateProjectDto {
  const shortcode = details.shortcode.trim();
  return {
    name: details.name.trim(),
    projectType,
    parentID: parentProjectID,
    ...(shortcode ? { shortcode } : {})
  };
}

export const CARRIER_PROJECT_TYPE = KnownCreateProjectDtoProjectType.Carrier;
export const REGION_PROJECT_TYPE = KnownCreateProjectDtoProjectType.Region;

/**
 * Step 2 of two, against the project from step 1. `servicing` is left off for a region, which
 * inherits what its organisation was granted.
 */
export function toCreateCarrierOrganisationDto(
  projectID: string,
  servicing?: CarrierServicingGrantInputDto[],
  loginMethod?: CourierLoginMethod
): CreateCarrierOrganisationDto {
  return {
    projectID,
    ...(servicing?.length ? { servicing } : {}),
    ...(loginMethod ? { loginMethod } : {})
  };
}
